// Projektweiter KI-Copilot: zeigt die Planungs-Readiness eines Projekts.
//
// Deterministisch (kein LLM, keine PII): liest nur Status/Counts aus presentation-
// Summary (Ertrag/Stringing/Wirtschaft), Konzept-Dokumentenliste und die vom Eltern-
// Element gelieferten Dach-Flags. Rendert die wiederverwendbare PlanningCopilot-UI.
import { useEffect, useState } from "react";
import { apiClient } from "@/lib/api-client.js";
import { buildProjectReadinessAdvice, type RoofCopilotAdvice } from "@energyos/roof-core";
import { normalizeRoofSemanticData } from "@energyos/shared-types";
import { PlanningCopilot } from "./PlanningCopilot.js";

interface PresentationSummary {
  latestSimulation?: { specificYieldKwhPerKwp?: number | null } | null;
  latestEconomics?: { npvEur?: number | null; paybackYears?: number | null; annualSavingsEurExpected?: number | null } | null;
  latestEngineering?: { numberOfStrings?: number | null; modulesTotal?: number | null } | null;
}

interface GeoResponse {
  buildings?: Array<{ roofSurfaces?: Array<{ roofType?: string; semanticJson?: unknown }> }>;
}

export function ProjectCopilot({ projectId }: { projectId: string }) {
  const [advice, setAdvice] = useState<RoofCopilotAdvice | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      // Status-/Count-Signale holen — KEINE Kundendaten in den Copilot-Kontext.
      const [pres, konzept, geo] = await Promise.all([
        apiClient.get<PresentationSummary>(`/projects/${projectId}/presentation`),
        apiClient.get<unknown[]>(`/projects/${projectId}/documents/konzept`),
        apiClient.get<GeoResponse>(`/projects/${projectId}/geospatial`),
      ]);
      if (cancelled) return;

      const sim = pres.success ? pres.data.latestSimulation : null;
      const eng = pres.success ? pres.data.latestEngineering : null;
      const eco = pres.success ? pres.data.latestEconomics : null;
      const konzeptCount = konzept.success && Array.isArray(konzept.data) ? konzept.data.length : 0;

      // Dach-Flags aus geospatial (nur Geometrie/Status, keine PII).
      const roofs = (geo.success ? geo.data.buildings ?? [] : [])
        .flatMap((b) => b.roofSurfaces ?? [])
        .filter((r) => r.roofType !== "obstacle");
      const roofSurfaceCount = roofs.length;
      const edgeTypes = roofs.flatMap((r) => normalizeRoofSemanticData(r.semanticJson).edges.map((e) => e.type));
      const hasFirstTraufe = edgeTypes.includes("first") && edgeTypes.includes("traufe");

      const yieldPresent = (sim?.specificYieldKwhPerKwp ?? 0) > 0;
      const stringingPresent = (eng?.numberOfStrings ?? 0) > 0 || (eng?.modulesTotal ?? 0) > 0;
      const calcPresent = eco != null && (eco.npvEur != null || eco.annualSavingsEurExpected != null);

      setAdvice(
        buildProjectReadinessAdvice({
          roofSurfaceCount,
          hasFirstTraufe,
          yieldPresent,
          stringingPresent,
          calcPresent,
          conceptPresent: konzeptCount > 0,
        }),
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  if (!advice) return null;
  return <PlanningCopilot advice={advice} title="KI-Copilot · Projekt-Readiness" />;
}
